// File: src/features/jobs/McpJobAnalysisPanel.tsx
// MCP-Enhanced Job Analysis Panel
//
// ✅ PROVIDES: Multi-AI job analysis, orchestration insights, and remediation suggestions
// ✅ INTEGRATES: ParaLex MCP Client with comprehensive job monitoring
// ✅ ENHANCES: Existing job detail modal with advanced MCP capabilities

import { useState, useEffect } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useAppStore } from '../../store/appStore';
import { Badge } from '@/components/ui/badge';
import { 
  getJobMonitorWithMcp, 
  analyzeJobWithMcp,
  getMcpHealthStatus,
  JobMonitorMcpData,
  McpHealthStatus
} from './mcpApi';
import { JobDetail } from './jobTypes';

interface McpJobAnalysisPanelProps {
  job: JobDetail;
  isVisible?: boolean;
  onAnalysisComplete?: (analysis: JobMonitorMcpData) => void;
  onAnalysisError?: (error: string) => void;
}

interface AnalysisState {
  isLoading: boolean;
  data: JobMonitorMcpData | null;
  error: string | null;
  mcpHealth: McpHealthStatus | null;
  lastUpdated: string | null;
}

export default function McpJobAnalysisPanel({
  job,
  isVisible = true,
  onAnalysisComplete,
  onAnalysisError
}: McpJobAnalysisPanelProps) {
  const { keycloak } = useKeycloak();
  const { currentFirmId } = useAppStore();
  
  const [analysis, setAnalysis] = useState<AnalysisState>({
    isLoading: false,
    data: null,
    error: null,
    mcpHealth: null,
    lastUpdated: null
  });

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    health: false,
    consensus: false,
    remediation: false,
    tools: false,
    trace: false
  });

  // Initialize MCP health check
  useEffect(() => {
    if (!keycloak?.token || !isVisible) return;

    const checkMcpHealth = async () => {
      try {
        const health = await getMcpHealthStatus(keycloak.token!);
        setAnalysis(prev => ({ ...prev, mcpHealth: health }));
      } catch (error) {
        console.warn('[McpJobAnalysisPanel] MCP health check failed:', error);
      }
    };

    checkMcpHealth();
  }, [keycloak?.token, isVisible]);

  // Auto-analyze failed jobs
  useEffect(() => {
    if (!keycloak?.token || !isVisible || !job || analysis.data) return;

    if (job.status === 'failed' && analysis.mcpHealth?.status === 'healthy') {
      handleAnalyzeJob('comprehensive');
    }
  }, [job, keycloak?.token, isVisible, analysis.mcpHealth]);

  const handleAnalyzeJob = async (analysisType: 'simple' | 'standard' | 'comprehensive' = 'comprehensive') => {
    if (!keycloak?.token || !currentFirmId) {
      setAnalysis(prev => ({ ...prev, error: 'Authentication required' }));
      return;
    }

    setAnalysis(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // Get comprehensive job monitoring data
      const monitorData = await getJobMonitorWithMcp(job.id, keycloak.token);
      
      // If enhanced analysis is available, run additional analysis
      let additionalAnalysis = null;
      if (monitorData.enhancedAnalysis && job.status === 'failed') {
        additionalAnalysis = await analyzeJobWithMcp({
          jobId: job.id,
          realmId: currentFirmId,
          analysisType,
          mcpStrategy: 'multi_ai'
        }, keycloak.token);
      }

      const finalData = {
        ...monitorData,
        additionalAnalysis
      };

      setAnalysis(prev => ({
        ...prev,
        isLoading: false,
        data: finalData,
        lastUpdated: new Date().toISOString()
      }));

      onAnalysisComplete?.(finalData);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Analysis failed';
      setAnalysis(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage
      }));
      onAnalysisError?.(errorMessage);
    }
  };

  const handleRefreshAnalysis = () => {
    setAnalysis(prev => ({ ...prev, data: null }));
    handleAnalyzeJob('comprehensive');
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getProviderBadgeVariant = (available: boolean) => {
    return available ? 'success' : 'outline';
  };



  if (!isVisible) return null;

  return (
    <div className="bg-gradient-to-br from-lex-accent-soft to-purple-50 dark:from-lex-accent-soft dark:to-purple-900/10 rounded-lg border border-lex-accent p-4 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">🤖</span>
          <div>
            <h3 className="text-lg font-semibold text-lex-accent">
              MCP Enhanced Analysis
            </h3>
            <p className="text-sm text-lex-accent">
              Multi-AI orchestration and comprehensive job insights
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {analysis.mcpHealth && (
            <Badge variant={analysis.mcpHealth.status === 'healthy' ? 'success' : 'destructive'}>
              MCP {analysis.mcpHealth.status}
            </Badge>
          )}
          
          {!analysis.isLoading && !analysis.data && (
            <button
              onClick={() => handleAnalyzeJob('comprehensive')}
              className="px-3 py-1 bg-lex-accent text-lex-accent-contrast rounded text-sm hover:bg-lex-accent-hover transition-colors"
              disabled={!analysis.mcpHealth || analysis.mcpHealth.status !== 'healthy'}
            >
              🧠 Analyze Job
            </button>
          )}
          
          {analysis.data && (
            <button
              onClick={handleRefreshAnalysis}
              className="px-3 py-1 bg-lex-accent text-lex-accent-contrast rounded text-sm hover:bg-lex-accent-hover transition-colors"
              disabled={analysis.isLoading}
            >
              🔄 Refresh
            </button>
          )}
        </div>
      </div>

      {/* MCP Health Status */}
      {analysis.mcpHealth && (
        <div className="mb-4">
          <button
            onClick={() => toggleSection('health')}
            className="flex items-center gap-2 text-sm font-medium text-lex-accent hover:text-lex-accent-hover"
          >
            {expandedSections.health ? '▼' : '▶'} MCP Provider Status
          </button>
          
          {expandedSections.health && (
            <div className="mt-2 p-3 bg-lex-accent-soft rounded border border-lex-accent">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(analysis.mcpHealth.mcpHealth).map(([provider, available]) => (
                  <div key={provider} className="flex items-center gap-2">
                    <Badge variant={getProviderBadgeVariant(available)}>
                      {provider}
                    </Badge>
                    <span className="text-xs">
                      {available ? '🟢' : '🔴'}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="mt-2 text-xs text-lex-accent">
                Capabilities: {Object.entries(analysis.mcpHealth.capabilities).filter(([,enabled]) => enabled).map(([cap]) => cap).join(', ')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Loading State */}
      {analysis.isLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-lex-accent"></div>
            <span className="text-lex-accent">
              Running multi-AI analysis...
            </span>
          </div>
        </div>
      )}

      {/* Error State */}
      {analysis.error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded text-sm">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
            <span>❌</span>
            <strong>Analysis Failed:</strong>
          </div>
          <p className="text-red-600 dark:text-red-400 mt-1">{analysis.error}</p>
        </div>
      )}

      {/* Analysis Results */}
      {analysis.data && (
        <div className="space-y-4">
          {/* AI Consensus */}
          {analysis.data.aiConsensus && (
            <div>
              <button
                onClick={() => toggleSection('consensus')}
                className="flex items-center gap-2 text-sm font-medium text-lex-accent hover:text-lex-accent-hover"
              >
                {expandedSections.consensus ? '▼' : '▶'} AI Consensus Analysis
                <Badge variant="secondary">
                  {Math.round(analysis.data.aiConsensus.confidence * 100)}% confidence
                </Badge>
              </button>
              
              {expandedSections.consensus && (
                <div className="mt-2 p-3 bg-lex-accent-soft rounded border border-lex-accent">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-lex-accent mb-2">
                        Agreement Score: {Math.round(analysis.data.aiConsensus.agreement * 100)}%
                      </h5>
                      <div className="w-full bg-lex-accent-soft rounded-full h-2">
                        <div
                          className="bg-lex-accent h-2 rounded-full" 
                          style={{ width: `${analysis.data.aiConsensus.agreement * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-lex-accent mb-2">
                        Key Recommendations
                      </h5>
                      <ul className="text-sm text-lex-accent space-y-1">
                        {analysis.data.aiConsensus.recommendations.slice(0, 3).map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span>•</span>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!analysis.data.enhancedAnalysis && (
            <div className="text-center py-4 text-lex-accent">
              <div className="text-2xl mb-2">⚡</div>
              <p className="text-sm">Basic job data available. MCP enhanced analysis not performed.</p>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!analysis.isLoading && !analysis.data && !analysis.error && analysis.mcpHealth?.status === 'healthy' && (
        <div className="text-center py-6">
          <div className="text-lex-accent text-4xl mb-2">🤖</div>
          <p className="text-lex-accent text-sm">
            Run MCP analysis to get AI-powered insights about this job
          </p>
          <button
            onClick={() => handleAnalyzeJob('comprehensive')}
            className="mt-3 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover transition-colors"
          >
            🧠 Start Analysis
          </button>
        </div>
      )}
    </div>
  );
} 